import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { Country } from 'app/shared/shared.types';
import { UtilService } from 'app/shared/util.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Artist, Label, Release, Store, Track } from '../releases/releases.types';
import { AdminStatsDTO, AnalyticsCategoryType, AnalyticsDataDTO, AnalyticsDataRow, AnalyticsDemographicsDTO, AnalyticsFilterConfig, AnalyticsFilterRequestDTO, AnalyticsFilterType, AnalyticsInsightsColumn, AnalyticsInsightsRequestDTO, AnalyticsInsightsResponseDTO, AnalyticsRequestDTO, PartnerStatsDTO, ReportRequestDTO, SortConfig, TimestampedData } from './analytics.types';

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private _userFilters: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _artistFilters: BehaviorSubject<Artist[] | null> = new BehaviorSubject(null);
    private _releaseFilters: BehaviorSubject<Release[] | null> = new BehaviorSubject(null);
    private _trackFilters: BehaviorSubject<Track[] | null> = new BehaviorSubject(null);
    private _storeFilters: BehaviorSubject<Store[] | null> = new BehaviorSubject(null);
    private _labelFilters: BehaviorSubject<Label[] | null> = new BehaviorSubject(null);

    constructor(private _httpClient: HttpClient, private _utilService: UtilService) {
    }

    get artistFilters$(): Observable<Artist[]> {
        return this._artistFilters.asObservable();
    }

    get userFilters$(): Observable<User[]> {
        return this._userFilters.asObservable();
    }

    get releaseFilters$(): Observable<Release[]> {
        return this._releaseFilters.asObservable();
    }

    get trackFilters$(): Observable<Track[]> {
        return this._trackFilters.asObservable();
    }

    get labelFilters$(): Observable<Label[]> {
        return this._labelFilters.asObservable();
    }

    get stores$(): Observable<Store[]> {
        return this._storeFilters.asObservable();
    }

    getDemographics(startDate: Date, endDate: Date, maxRows: number) {
        let body: AnalyticsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        return this._httpClient.post('api/statistics/demographics', body).pipe(
            map((response: AnalyticsDemographicsDTO) => {
                response.countries.entities.forEach(row => {
                    if (!row || !(row as Country).alpha2) {
                        row = {
                            title: "Unknown",
                        }
                    } else {
                        row.title = this._utilService.getCountryName(row as Country);
                    }
                });

                return response;
            })
        );
    }

    getPartnerStats(user: User) {
        if (user?.role !== "partner") {
            return of(false);
        }

        return this._httpClient.get<PartnerStatsDTO>('api/statistics/partner-stats');
    }

    getAdminStats(user: User) {
        if (user?.role !== "admin") {
            return of(false);
        }

        return this._httpClient.get<AdminStatsDTO>('api/statistics/admin-stats');
    }

    getTimeSeries(category: AnalyticsCategoryType | 'aggregated' | 'videos', startDate: Date, endDate: Date, maxRows: number, filter?: AnalyticsFilterConfig, includeSocials?: boolean): Observable<AnalyticsDataDTO> {
        let body: AnalyticsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        if (filter !== undefined) {
            body.filter = filter.toDTO()
        }

        if (includeSocials !== undefined) {
            body.includeSocials = includeSocials;
        }

        return this._httpClient.post('api/statistics/time-series/' + category, body).pipe(
            map((response: AnalyticsDataRow[]) =>this.analyticsRowsPostProcessing(response))
        );
    }

    getInsights(startDate: Date, endDate: Date, baseColumn: AnalyticsInsightsColumn, detailColumn: AnalyticsInsightsColumn, filter?: AnalyticsFilterConfig): Observable<any> {
        let body: AnalyticsInsightsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            baseColumn: baseColumn,
            detailColumn: detailColumn,
        }

        if (filter !== undefined) {
            body.filter = filter.toDTO()
        }

        return this._httpClient.post<AnalyticsInsightsResponseDTO>('api/statistics/insights', body);
    }

    getTimeSeriesForReport(reportUuid: string, category: AnalyticsCategoryType, reportType: 'royalties' | 'sales' | 'user-royalties', maxRows: number, currency?: string, filter?: AnalyticsFilterConfig, sort?: SortConfig, comparison?: string): Observable<AnalyticsDataDTO> {
        let body: ReportRequestDTO = {
            reportUuid: reportUuid,
            reportType: reportType,
            maxRows: maxRows,
            currency: currency,
        }

        if (filter !== undefined) {
            body.filter = filter.toDTO()
        }

        if (sort !== undefined) {
            body.sortBy = sort.sortBy;
            if (sort.sortDirection === "") {
                sort.sortDirection = "desc";
            }
            body.sortDirection = sort.sortDirection;
        }

        if (comparison !== undefined) {
            body.comparison = comparison;
        }

        return this._httpClient.post('api/statistics/report/' + category, body).pipe(
            map((response: AnalyticsDataRow[]) =>this.analyticsRowsPostProcessing(response))
        );
    }

    getTimeSeriesForArtificialStreams(category: AnalyticsCategoryType, maxRows: number, startDate: Date, endDate: Date, filter?: AnalyticsFilterConfig, sort?: SortConfig, comparison?: string): Observable<AnalyticsDataDTO> {
        let body: ReportRequestDTO = {
            reportType: "artificial",
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        if (filter !== undefined) {
            body.filter = filter.toDTO()
        }

        if (sort !== undefined) {
            body.sortBy = sort.sortBy;
            if (sort.sortDirection === "") {
                sort.sortDirection = "desc";
            }
            body.sortDirection = sort.sortDirection;
        }

        if (comparison !== undefined) {
            body.comparison = comparison;
        }

        return this._httpClient.post('api/statistics/report/' + category, body).pipe(
            map((response: AnalyticsDataRow[]) =>this.analyticsRowsPostProcessing(response))
        );
    }

    private analyticsRowsPostProcessing(data: AnalyticsDataRow[]) {
        let wrappedResponse: AnalyticsDataDTO = {
            data: data
        }

        wrappedResponse.data.forEach(row => {
            if (row.metadata?.country) {
                row.metadata.title = this._utilService.getCountryName(row.metadata.country);
            }
        });

        wrappedResponse.data.forEach((row, i) => {
            wrappedResponse.data[i].totals = this.getTotalsForRow(row);
        });

        return wrappedResponse;
    }

    getTotalsForRow(row: AnalyticsDataRow) {
        let totals = {
            revenue: row.stats.revenue.reduce((sum, x) => sum + x),
            streams: row.stats.streams.reduce((sum, x) => sum + x),
            downloads: row.stats.downloads.reduce((sum, x) => sum + x),
            creations: row.stats.creations.reduce((sum, x) => sum + x),
            pps: row.stats.pps.reduce((sum, x) => sum + x),
            likes: row.stats.likes.reduce((sum, x) => sum + x),
            comments: row.stats.comments.reduce((sum, x) => sum + x),
            shares: row.stats.shares.reduce((sum, x) => sum + x),
            favorites: row.stats.favorites.reduce((sum, x) => sum + x),
            avg_duration: 0,
        }
        let totalDurationByStreams = row.stats.avg_duration.reduce((sum, x, i) => sum + x * row.stats.streams[i], 0);
        totals.avg_duration = totalDurationByStreams / totals.streams;
        return totals;
    }

    addTimestampedData(row: AnalyticsDataRow) {
        let createTimestamps = (values: number[], labels: string[]): TimestampedData[] => {
            if (!values) {
                return [];
            }
            return values.map((value, i) => {
                if (!labels[i]) {
                    return null;
                }
                let dateElements = labels[i].split('-');
                let dateString = dateElements[0] + '-' + dateElements[1].padStart(2, "0") + '-' + dateElements[2].padStart(2, "0");
                dateString = dateString + 'T00:00:00.000Z';
                if (value === null) {
                    value = 0;
                }
                return { x: dateString, y: value }
            });
        }

        row.timestampedStats = {
            revenuePerDay: createTimestamps(row.stats.revenue, row.stats.labels),
            streamsPerDay: createTimestamps(row.stats.streams, row.stats.labels),
            artificialsPerDay: createTimestamps(row.stats.artificials, row.stats.labels),
            artificialPercentagesPerDay: createTimestamps(row.stats.artificialsPercentage, row.stats.labels),
            downloadsPerDay: createTimestamps(row.stats.downloads, row.stats.labels),
            creationsPerDay: createTimestamps(row.stats.creations, row.stats.labels),
        }

        return row;
    }

    getUserFilters(filter: AnalyticsFilterConfig, search?: string): Observable<User[]> {
        return this.getFilters<User>('users', filter, search).pipe(
            tap(data => this._userFilters.next(data))
        )
    }

    getArtistFilters(filter: AnalyticsFilterConfig, search?: string): Observable<Artist[]> {
        return this.getFilters<Artist>('artists', filter, search).pipe(
            tap(data => this._artistFilters.next(data))
        )
    }

    getReleaseFilters(filter: AnalyticsFilterConfig, search?: string): Observable<Release[]> {
        return this.getFilters<Release>('releases', filter, search).pipe(
            tap(data => this._releaseFilters.next(data))
        )
    }

    getTrackFilters(filter: AnalyticsFilterConfig, search?: string): Observable<Track[]> {
        return this.getFilters<Track>('tracks', filter, search).pipe(
            tap(data => this._trackFilters.next(data))
        )
    }

    getLabelFilters(filter: AnalyticsFilterConfig, search?: string): Observable<Label[]> {
        return this.getFilters<Label>('labels', filter, search).pipe(
            tap(data => this._labelFilters.next(data))
        )
    }

    getStores(filter: AnalyticsFilterConfig): Observable<Store[]> {
        return this.getFilters<Store>('stores', filter).pipe(
                tap(data => this._storeFilters.next(data))
            )
    }

    getFilters<T>(type: AnalyticsFilterType | string, filter: AnalyticsFilterConfig, search?: string): Observable<T[]> {
        let requestBody: AnalyticsFilterRequestDTO = {
            filter: filter.toDTO(),
            search: search
        }

        if (!search) {
            delete(requestBody.search);
        }

        return this._httpClient.post<T[]>('api/statistics/filters/'+type, requestBody).pipe(
            map((data) => {
                return data.sort((a: any, b: any) => {
                    return a.title?.toLowerCase().localeCompare(b.title?.toLowerCase());
                });
            })
        );
    }
}
